import { CryptoManager, LeviCryptoManagerSecret } from './cryptoManager'
import { localStorageDataType } from './local-storage.service'

export interface ILocalStorageManagerUtilities {
  WriteEncryptedData: <T>(key: localStorageDataType, obj?: T | null) => void
  ReadEncryptedData: <T>(key: localStorageDataType) => T | null | string
  ReadEncryptedPlainText: <T>(key: localStorageDataType) => T | null | string | number
  DeleteData: <T>(key: localStorageDataType) => void
}

class LocalStorageEncryptedDataUtils extends CryptoManager implements ILocalStorageManagerUtilities {
  private convertObject(obj: any) {
    try {
      return JSON.parse(obj)
    } catch {
      return obj
    }
  }

  public DeleteData(key: localStorageDataType): void {
    const keyAsString = this.KeyAsString(key)
    localStorage.removeItem(keyAsString)
  }

  public WriteEncryptedData<T>(key: localStorageDataType, value: T): void {
    localStorage.setItem(key, this.encrypt(value))
  }

  public ReadEncryptedData<T>(key: localStorageDataType): T {
    const encryptedData = localStorage.getItem(key) ?? ''
    if (!encryptedData) return encryptedData as T
    return this.convertObject(this.decryptPlainText(encryptedData)) as T
  }

  public ReadEncryptedPlainText<T>(key: localStorageDataType): T | null | string | number {
    const keyAsString = this.KeyAsString(key)
    const encryptedData = localStorage.getItem(keyAsString)
    if (!encryptedData) {
      return null
    }

    return this.decryptPlainText(encryptedData)
  }

  public getCertificateToken() {
    let expirationDate!: string
    let expiresIn!: string
    const hasEncryptedToken = localStorage.getItem('verifica_token-encrypted')
    const hasEncryptedExpirationDate = localStorage.getItem('verifica_token_expiration_date-encrypted')
    if (hasEncryptedExpirationDate) {
      expirationDate = this.decryptPlainText(hasEncryptedExpirationDate)
    } else {
      const hasExpirationDate = localStorage.getItem('verifica_token_expiration_date')
      expirationDate = !hasExpirationDate ? undefined : JSON.parse(hasExpirationDate)
    }
    const hasEncryptedExpiresIn = localStorage.getItem('verifica_token_expires_in-encrypted')
    if (hasEncryptedExpiresIn) {
      expiresIn = this.decryptPlainText(hasEncryptedExpiresIn)
    } else {
      const hasExpiresIn = localStorage.getItem('verifica_token_expires_in')
      expiresIn = !hasExpiresIn ? undefined : JSON.parse(hasExpiresIn)
    }
    return {
      token: hasEncryptedToken
        ? this.decryptPlainText(hasEncryptedToken)
        : JSON.parse(localStorage.getItem('verifica_token') as string),
      expirationDate,
      expiresIn
    }
  }

  private KeyAsString(key: localStorageDataType): string {
    return key as string
  }
}

export const LocalStorageManager = new LocalStorageEncryptedDataUtils(LeviCryptoManagerSecret)
